import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Desktop } from '@phosphor-icons/react';
import { BrowserUtility, CommonUtility, localStorageKeys, theme } from '../utility';
import { useAuth } from './auth';
import { WarehouseListHook } from '../hooks';

interface IWarehouse {
  _id: string;
  name: string;
  shipnodeStations?: IStation[];
}

interface IStation {
  stationId: string;
  stationName: string;
  /** The ShipNode station's machine id; what the packing screen prints to. */
  computerId?: string;
  printerId?: string;
  status?: string;
}

interface IPackingStationCache {
  warehouseId?: string;
  stationId?: string;
}

const savePackingStationCache = (warehouseId: string, stationId: string): void => {
  BrowserUtility.saveObj(localStorageKeys.packingStation, {
    warehouseId,
    stationId,
  });
};

const resolveStationId = (
  warehouseId: string,
  stationOptions: IOption<IStation>[],
): string => {
  const cached = BrowserUtility.getObj<IPackingStationCache>(
    localStorageKeys.packingStation,
  );

  const stationExists = (id: string) => stationOptions.some(x => x.value === id);

  if (cached?.stationId) {
    const matchesWarehouse = !cached.warehouseId || cached.warehouseId === warehouseId;

    if (matchesWarehouse && stationExists(cached.stationId)) {
      return cached.stationId;
    }
  }

  return stationOptions[0].value;
};

interface IOption<T = any> {
  text: string | ReactNode;
  content?: ReactNode;
  key: string;
  value: string;
  data?: T;
}

interface IGlobalContext {
  showSidebar: boolean;
  setShowSidebar: React.Dispatch<React.SetStateAction<boolean>>;
  warehouseLoading: boolean;
  warehouseOptions: IOption[];
  warehouseId: string | undefined;
  changeWarehouse: (id: string) => void;
  currentWarehouse?: IWarehouse;
  refreshWarehouseList: () => void;
  stationId: string;
  changeStation: (id: string) => void;
  stationOptions: IOption<IStation>[];
  currentStation?: IStation;
}

const GlobalContext = createContext<IGlobalContext | undefined>(undefined);

interface IGlobalProviderProps {
  children: ReactNode;
}

export function GlobalProvider({ children }: IGlobalProviderProps) {
  const [showSidebar, setShowSidebar] = useState(true);
  const { isAuthenticated } = useAuth();

  const [stationId, setStationId] = useState('');
  const [warehouseId, setWarehouseId] = useState<string>();

  const {
    data: warehouseData,
    loading: warehouseLoading,
    refresh,
  } = WarehouseListHook(isAuthenticated, true);

  const defaultWarehouseId = useMemo(
    () => (CommonUtility.isValidArray(warehouseData) ? warehouseData[0]._id : null),
    [warehouseData],
  );

  const cacheKey = localStorageKeys.warehouseId;

  useEffect(() => {
    if (warehouseLoading) return;
    const cache = BrowserUtility.getObj(cacheKey);
    // check if cached warehouse id exists in the warehouse data
    let toSet = defaultWarehouseId;

    if (cache?.id) {
      const existsInData = warehouseData?.some((wh: IWarehouse) => wh._id === cache?.id);

      if (existsInData) {
        toSet = cache?.id;
      }
    }

    if (toSet && toSet !== warehouseId) {
      setWarehouseId(toSet);
    }
  }, [defaultWarehouseId, warehouseData, warehouseLoading]);

  const changeWarehouse = (id: string) => {
    setWarehouseId(id);
    BrowserUtility.saveObj(cacheKey, { id });
  };

  const warehouseOptions = useMemo<IOption[]>(() => {
    if (CommonUtility.isValidArray(warehouseData)) {
      return warehouseData.map((wh: IWarehouse) => ({
        text: wh?.name,
        key: wh?._id,
        value: wh?._id,
      }));
    }
    return [];
  }, [warehouseData]);

  const currentWarehouse = useMemo<IWarehouse | undefined>(
    () => warehouseData?.find((wh: IWarehouse) => wh._id === warehouseId),
    [warehouseData, warehouseId],
  );

  const stationOptions = useMemo<IOption<IStation>[]>(
    () =>
      currentWarehouse?.shipnodeStations?.map(x => ({
        text: (
          <div className="d-flex align-items-center">
            <Desktop color={theme.colors.grey} size={16} />
            <span className="ml-1">{x.stationName}</span>
          </div>
        ),
        content: (
          <div className="d-flex align-items-center">
            <Desktop color={theme.colors.grey} size={16} />
            <span className="ml-1">{x.stationName}</span>
          </div>
        ),
        value: x.stationId,
        key: x.stationId,
        data: x,
      })) ?? [],
    [currentWarehouse],
  );

  useEffect(() => {
    if (warehouseLoading || !warehouseId) {
      return;
    }

    if (!CommonUtility.isValidArray(stationOptions)) {
      setStationId('');
      return;
    }

    const resolved = resolveStationId(warehouseId, stationOptions);
    setStationId(resolved);
    savePackingStationCache(warehouseId, resolved);
  }, [stationOptions, warehouseLoading, warehouseId]);

  const changeStation = (nextStationId: string) => {
    setStationId(nextStationId);
    if (warehouseId) {
      savePackingStationCache(warehouseId, nextStationId);
    }
  };

  const currentStation = useMemo<IStation | undefined>(
    () => currentWarehouse?.shipnodeStations?.find(x => x.stationId === stationId),
    [currentWarehouse, stationId],
  );

  return (
    <GlobalContext.Provider
      value={{
        showSidebar,
        setShowSidebar,
        warehouseLoading,
        warehouseOptions,
        warehouseId,
        changeWarehouse,
        currentWarehouse,
        refreshWarehouseList: refresh,
        changeStation,
        stationId,
        stationOptions,
        currentStation,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
}

export const useGlobalContext = (): IGlobalContext => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobalContext must be used within a GlobalProvider');
  }
  return context;
};
